import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { Show } from '@side/pantry';
import type { ReactNode } from 'react';
import {
  CoverPhoto,
  ListingDetailLine,
  THUMB_HEIGHT,
  THUMB_WIDTH,
} from './helpers';

type Props = {
  listingId: string;
  coverPhotoUrl: string | null;
  fullAddress: string;
  interactionStatus: string;
  mlsNumber: string | null;
  sellers: string;
  action?: ReactNode;
  loading: boolean;
  showMls?: boolean;
};

const containerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  px: 2,
  py: 1.5,
  border: '1px solid',
  borderColor: 'other.outlineBorder',
  borderRadius: '2px',
  backgroundColor: 'grey.100',
} as const;

const Loading = () => (
  <Box sx={containerSx}>
    <Skeleton
      variant="rectangular"
      width={THUMB_WIDTH}
      height={THUMB_HEIGHT}
      sx={{ borderRadius: 1, flexShrink: 0 }}
    />
    <Stack sx={{ flex: 1, gap: 0.5 }}>
      <Skeleton width={220} height={20} />
      <Skeleton width={280} height={16} />
    </Stack>
  </Box>
);

export const ListingSummaryDesktop = ({
  listingId,
  coverPhotoUrl,
  fullAddress,
  interactionStatus,
  mlsNumber,
  sellers,
  action,
  loading,
  showMls = false,
}: Props) => {
  if (loading) return <Loading />;

  return (
    <Box sx={containerSx}>
      <CoverPhoto
        coverPhotoUrl={coverPhotoUrl}
        id={listingId}
        sizes={`${THUMB_WIDTH}px`}
        sx={{
          flexShrink: 0,
          width: THUMB_WIDTH,
          height: THUMB_HEIGHT,
          borderRadius: 1,
        }}
      />

      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
          {fullAddress}
        </Typography>
        <ListingDetailLine
          interactionStatus={interactionStatus}
          mlsNumber={showMls ? mlsNumber : null}
          formattedSellers={sellers}
        />
      </Stack>

      <Show when={action}>
        <Box sx={{ flexShrink: 0 }}>{action}</Box>
      </Show>
    </Box>
  );
};
